import { useEffect } from 'react'
import { Box } from '@mui/material'
import { SelectedQuestionnairesForVersions } from './SelectedQuestionnairesForVersions'
import { VersionList } from './VersionList'
import { StorePerformanceItem, QueryMode } from '../types'

interface VersionSelectionPanelProps {
  queryMode: QueryMode
  selectedQuestionnairesWithVersions: StorePerformanceItem[]
  selectedVersionsByQuestionnaire: Record<string, string[]>
  activeQuestionnaireId: string | null
  onSelectQuestionnaire: (id: string) => void
  onToggleVersion: (questionnaireId: string, versionId: string) => void
  onToggleAllVersions: (questionnaireId: string) => void
  t: (key: string, options?: Record<string, unknown>) => string
  colors: { color9: string; color13: string; color45: string }
}

const VersionSelectionPanel = ({
  queryMode,
  selectedQuestionnairesWithVersions,
  selectedVersionsByQuestionnaire,
  activeQuestionnaireId,
  onSelectQuestionnaire,
  onToggleVersion,
  onToggleAllVersions,
  t,
  colors,
}: VersionSelectionPanelProps) => {
  const activeQuestionnaire = selectedQuestionnairesWithVersions.find(
    (q) => q.id === activeQuestionnaireId
  )
  const activeVersions = activeQuestionnaire?.versions ?? []
  const selectedVersionIds = activeQuestionnaireId
    ? selectedVersionsByQuestionnaire[activeQuestionnaireId] ?? []
    : []

  const totalSelectedExecutions = Object.values(selectedVersionsByQuestionnaire).reduce(
    (total, versions) => total + versions.length,
    0
  )

  useEffect(() => {
    const isActiveValid = selectedQuestionnairesWithVersions.some((q) => q.id === activeQuestionnaireId)
    if (!isActiveValid && selectedQuestionnairesWithVersions.length > 0) {
      onSelectQuestionnaire(selectedQuestionnairesWithVersions[0].id)
    }
  }, [selectedQuestionnairesWithVersions, activeQuestionnaireId, onSelectQuestionnaire])

  useEffect(() => {
    if (!activeQuestionnaireId || activeVersions.length !== 1) return

    const onlyVersionId = String(activeVersions[0].id)
    const currentSelected = selectedVersionsByQuestionnaire[activeQuestionnaireId] || []

    if (!currentSelected.includes(onlyVersionId)) {
      onToggleVersion(activeQuestionnaireId, onlyVersionId)
    }
  }, [activeQuestionnaireId, activeVersions, onToggleVersion])

  const versionSelectionType: 'checkbox' | 'radio' =
    queryMode === 'one-unit-many-questionnaires' ? 'checkbox' : 'radio'

  return (
    <Box sx={{ mt: '16px' }}>
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          minHeight: '320px',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: '300px',
            borderRadius: '1px',
            boxShadow: `0 0 2px 0px ${colors.color13}`,
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SelectedQuestionnairesForVersions
            questionnaires={selectedQuestionnairesWithVersions}
            activeQuestionnaireId={activeQuestionnaireId}
            onSelectQuestionnaire={onSelectQuestionnaire}
            t={t}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: '300px',
            borderRadius: '1px',
            boxShadow: `0 0 2px 0px ${colors.color13}`,
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <VersionList
            versions={activeVersions}
            selectedVersionIds={selectedVersionIds}
            totalSelectedExecutions={totalSelectedExecutions}
            queryMode={queryMode}
            onToggleVersion={(versionId) => {
              if (activeQuestionnaireId) {
                onToggleVersion(activeQuestionnaireId, versionId)
              }
            }}
            onToggleAllVersions={() => {
              if (activeQuestionnaireId) {
                onToggleAllVersions(activeQuestionnaireId)
              }
            }}
            t={t}
          />
        </Box>
      </Box>
    </Box>
  )
}

export { VersionSelectionPanel }
